import React, { useState, useMemo } from 'react';
import { Filter } from 'lucide-react';
import DetailSection from '@components/common/DetailSection';
import { AssessmentItem, AssessmentStatus } from '@app-types';
import ProcessItem from './ProcessItem';
import AssessmentFrameworkFilters from './AssessmentFrameworkFilters';
import styles from './AssessmentDomains.module.css';

interface DomainGroup {
  name: string;
  description?: string;
  processes: AssessmentItem[];
}

interface AssessmentDomainsProps {
  assessmentItems: AssessmentItem[];
  canUpdate?: boolean;
  onUpdateItem?: () => void;
  contractNumber?: string;
  supplierName?: string;
  assessmentStatus?: AssessmentStatus;
  assessorId?: number | null;
}

const AssessmentDomains: React.FC<AssessmentDomainsProps> = ({ assessmentItems, canUpdate = false, onUpdateItem, contractNumber, supplierName, assessmentStatus, assessorId }) => {
  const [filteredItems, setFilteredItems] = useState<AssessmentItem[]>(assessmentItems);

  // Group filtered items by domain
  const domainGroups: DomainGroup[] = useMemo(() => {
    const groupedMap = new Map<string, DomainGroup>();

    filteredItems.forEach((item) => {
      const domainName = item.domain_name;
      if (!groupedMap.has(domainName)) {
        groupedMap.set(domainName, {
          name: domainName,
          description: item.domain_description,
          processes: []
        });
      }
      groupedMap.get(domainName)!.processes.push(item);
    });

    return Array.from(groupedMap.values());
  }, [filteredItems]);

  // Handle filtered items change from filters component
  const handleFilteredItemsChange = (newFilteredItems: AssessmentItem[]) => {
    setFilteredItems(newFilteredItems);
  };

  if (assessmentItems.length === 0) {
    return (
      <DetailSection title="Assessment Framework">
        <div className={styles.emptyState} data-test-id="assessment-framework-section">
          <p>No assessment items found. The assessment framework has not been generated yet.</p>
        </div>
      </DetailSection>
    );
  }

  return (
    <DetailSection title="Assessment Framework">
      <div data-test-id="assessment-framework-section">
        {/* Dedicated Assessment Framework Filters */}
        <AssessmentFrameworkFilters
          assessmentItems={assessmentItems}
          onFilteredItemsChange={handleFilteredItemsChange}
        />

        {/* Domain Groups - Always Expanded */}
        <div className={styles.domainsContainer}>
          {domainGroups.length === 0 ? (
            <div className={styles.noResults}>
              <Filter size={48} style={{ color: 'var(--color-gray-400)' }} />
              <h4>No processes match your filters</h4>
              <p>Try adjusting your search criteria or clearing filters to see more results.</p>
            </div>
          ) : (
            domainGroups.map((domain) => {
              const stats = {
                total: domain.processes.length,
                implemented: domain.processes.filter((p) => p.evaluation === 'Implemented').length,
                implementedNeedsEvidence: domain.processes.filter(
                  (p) => p.evaluation === 'Implemented (Needs Evidence)'
                ).length,
                gaps: domain.processes.filter((p) => p.evaluation === 'Gap').length,
                notApplicable: domain.processes.filter((p) => p.evaluation === 'Not Applicable').length,
                notAssessed: domain.processes.filter((p) => p.evaluation === 'Not Assessed').length,
                remediated: domain.processes.filter((p) => p.evaluation === 'Remediated').length
              };

              return (
                <div key={domain.name} className={styles.domainCard} data-test-id="domain-card">
                  <div className={styles.domainHeaderStatic}>
                    <div className={styles.domainInfo}>
                      <h3>{domain.name}</h3>
                      {domain.description && <p className={styles.domainDescription}>{domain.description}</p>}
                    </div>

                    <div className={styles.domainStats}>
                      <div className={styles.domainCount}>{stats.total} processes</div>
                      <div className={styles.domainProgress}>
                        <span className={styles.statImplemented}>{stats.implemented}</span>
                        {stats.implementedNeedsEvidence > 0 && (
                          <span className={styles.statNeedsEvidence}>{stats.implementedNeedsEvidence}</span>
                        )}
                        {stats.remediated > 0 && <span className={styles.statRemediated}>{stats.remediated}</span>}
                        <span className={styles.statGaps}>{stats.gaps}</span>
                        <span className={styles.statPending}>{stats.notAssessed}</span>
                        {stats.notApplicable > 0 && <span className={styles.statNa}>{stats.notApplicable}</span>}
                      </div>
                    </div>
                  </div>

                  {/* Always show processes - no expand/collapse */}
                  <div className={styles.domainProcesses}>
                    {domain.processes.map((process) => (
                      <ProcessItem key={process.id} process={process} canUpdate={canUpdate} onUpdate={onUpdateItem} contractNumber={contractNumber} supplierName={supplierName} assessmentStatus={assessmentStatus} assessorId={assessorId} />
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </DetailSection>
  );
};

export default AssessmentDomains;
